/* eslint-disable react/jsx-pascal-case */
import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

// Public Layout Components (eager — shown on every public page)
import Header from "./Header";
import Footer from "./Footer1";
import ScrollToTop from "./ScrollToTop.js";

// Auth context (eager — wraps the whole app)
import { AuthProvider } from "./context/AuthContext";

// Site-wide default SEO (eager — tiny; gives every public page clean tags)
import DefaultSeo from "./components/DefaultSeo";

// Cookie consent banner (eager — tiny; must show on first paint of any page)
import CookieConsent from "./components/CookieConsent";

// GA4 + Clarity (eager — tiny; loads nothing until IDs are set at build time
// AND the visitor allows analytics cookies in the banner above)
import Analytics from "./components/Analytics";

// Services tab URLs (/<category>/<slug>) — shared with Services.js / Header / Home
import { serviceTabPath } from "./utils/serviceTabs";

// Home is EAGER (not lazy): it's the landing page, so loading it in the main
// bundle avoids a Suspense-fallback flash that pushed the footer down and caused
// a large Cumulative Layout Shift (CLS). All other pages stay lazy/code-split.
import Home from "./Home";

// Public Pages (lazy — each becomes its own chunk, loaded only when visited)
const About = lazy(() => import("./About"));
const Internship = lazy(() => import("./Intern"));
const Engineering = lazy(() => import("./Engineering"));
const Life = lazy(() => import("./Life"));
const Customer = lazy(() => import("./Customer"));
const Pharma = lazy(() => import("./Pharma"));
const Semiconductor = lazy(() => import("./Semiconductor"));
const Contact = lazy(() => import("./Contact"));
const Besto_Services = lazy(() => import("./Besto_Services.js"));
const Blogs = lazy(() => import("./blogscomponents/Blogs.js"));
const BlogDetails1 = lazy(() => import("./Blogdetails1.js"));
const Details = lazy(() => import("./Details.js"));
const Blogdetails2 = lazy(() => import("./Blogdetails2.js"));
const Blogdetails3 = lazy(() => import("./Blogdetails3.js"));
const Blogdetails4 = lazy(() => import("./Blogdetails4.js"));
const Blogdetails5 = lazy(() => import("./Blogdetails5.js"));
const Blogdetails6 = lazy(() => import("./Blogdetails6.js"));
const Blogdetails7 = lazy(() => import("./Blogdetails7.js"));
const Blogdetails8 = lazy(() => import("./Blogdetails8.js"));
const Blogdetails9 = lazy(() => import("./Blogdetails9.js"));
const CareerLanding = lazy(() => import("./CareerLanding.js"));
const IndustryLanding = lazy(() => import("./IndustryLanding.js"));
const Products = lazy(() => import("./Products.js"));
const Tabbing = lazy(() => import("./Tabbing.js"));
const Barde = lazy(() => import("./Barde.js"));
const TCApp = lazy(() => import("./TCApp.js"));
const BestowalWeb = lazy(() => import("./BestowalWeb.js"));
const GoogleLogin = lazy(() => import("./blogscomponents/GoogleLogin.js"));
const BlogDetails = lazy(() => import("./blogscomponents/BlogDetails.js"));
const BlogTagSearch = lazy(() => import("./blogscomponents/BlogTagsearch.js"));
const Testimonials = lazy(() => import("./Testomoniall.js"));
const TestimonialDetail = lazy(() => import("./TestimonialDetail.js"));
const EmployeeAppreciationDetail = lazy(() => import("./EmployeeAppreciationDetail.js"));
const Al = lazy(() => import("./Ai.jsx"));
const Aeronautical = lazy(() => import("./Aeronautical.js"));
const Electronics = lazy(() => import("./Electronics.js"));
const Agrochemical = lazy(() => import("./Agrochemical.js"));
const Chemical = lazy(() => import("./Chemical.js"));
const Manufacturing = lazy(() => import("./Manufacturing.js"));
const Fintech = lazy(() => import("./Fintech.js"));
const Education = lazy(() => import("./Education.js"));
const Sports = lazy(() => import("./Sports.js"));
const Packaging = lazy(() => import("./Packaging.js"));
const Pharmaceutical = lazy(() => import("./Pharmaceutical.js"));
const FMCG = lazy(() => import("./FMCG.js"));
const Retail = lazy(() => import("./Retail.js"));
const Semiconductor1 = lazy(() => import("./Semiconductor1.js"));
const AutomobileIndustry = lazy(() => import("./AutomobileIndustry.js"));
const Industry = lazy(() => import("./Industry.js"));
const Services = lazy(() => import("./Services"));
const AskOchat = lazy(() => import("./AskOchat.js"));
const Zenetick_AI = lazy(() => import("./Zenetick_AI.js"));
const PredoSolution = lazy(() => import("./PredoSolution.js"));
const BestoFlow = lazy(() => import("./BestoFlow.js"));
const BestoJyotish = lazy(() => import("./BestoJyotish.js"));
const Casestudyinfo = lazy(() => import("./blogscomponents/Casestudyinfo.js"));
const NewsDetails = lazy(() => import("./blogscomponents/NewsDetails.js"));
const PodcastInfo = lazy(() => import("./blogscomponents/PodcastInfo.js"));
const PolicyPage = lazy(() => import("./PolicyPage.js"));
const SearchResults = lazy(() => import("./SearchResults.js"));
const SuccessStories = lazy(() => import("./SuccessStories.js"));
const SuccessStoryDetail = lazy(() => import("./SuccessStoryDetail.js"));
const NotFound = lazy(() => import("./NotFound.js"));

// Old service pages that were removed (nothing on the site linked to them).
// Their URLs may still be indexed, so each one redirects to the closest live
// service tab instead of showing a 404. Backups: backups/remove-*-2026-10-09.
const WEB_TAB = serviceTabPath("/digital-experience-engagement", "web");
const MOBILE_TAB = serviceTabPath("/digital-experience-engagement", "mobile");
const SAP_TAB = serviceTabPath("/digital-core-transformation", "sap");
const IBP_TAB = serviceTabPath("/digital-core-transformation", "ibp");
const STAFF_TAB = serviceTabPath("/flexible-talent-delivery-models", "staff");
// Old Insights URLs whose pages were dead ends (no working data source):
// /relatedblog/:slug → the blog itself. Used by the routes below.
const BlogSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={slug ? `/blogs/${slug}` : "/blogs"} replace />;
};

const REMOVED_PAGE_REDIRECTS = {
  "/java": WEB_TAB,
  "/dotnet": WEB_TAB,
  "/node": WEB_TAB,
  "/angular": WEB_TAB,
  "/web-development": WEB_TAB,
  "/php": WEB_TAB,
  "/wordpress": WEB_TAB,
  "/Ui-Ux": WEB_TAB,
  "/native": MOBILE_TAB,
  "/android": MOBILE_TAB,
  "/ios": MOBILE_TAB,
  "/Mobile-development": MOBILE_TAB,
  "/digital": "/bestoservice",
  "/seo": "/bestoservice",
  "/monitoring": SAP_TAB,
  "/cloud": SAP_TAB,
  "/sapbusiness": SAP_TAB,
  "/sap": IBP_TAB,
  "/supply-chain-management-solution": IBP_TAB,
  "/resource": STAFF_TAB,
  "/It-staff-solution": STAFF_TAB,
  "/rpa": serviceTabPath("/intelligent-industry-solutions", "aiml"),
  // Removed 2026-10-11 (backups/remove-history-page-*, remove-ecommerce-page-*).
  "/history": "/about",
  "/e-commerce": WEB_TAB,
  // Besto PMS renamed to BestoFlow 2026-10-11 (backups/bestoflow-rename-*).
  "/besto_pms": "/bestoflow",
  // Removed 2026-10-11 (backups/review-fixes-2026-10-11): /careers duplicated
  // /careerlanding; the mock sign-in/application pages never sent anything
  // ("Apply Now" on a job is now an email link).
  "/careers": "/careerlanding",
  "/login": "/careerlanding",
  "/applicationform": "/careerlanding",
  "/createaccount": "/careerlanding",
  "/forgotpassword": "/careerlanding",
  // Old hard-coded job cards (/career) and their mock apply form (/modal).
  "/career": "/careerlanding",
  "/modal": "/careerlanding",
  // Removed 2026-10-15 (backups/remove-action-page-*): leftover "Reviews" stub.
  "/action": "/testimonials",
};

// Admin Components (lazy — the whole admin CMS, incl. MUI + react-quill, loads
// only under /admin and is never shipped to public visitors).
const adminLazy = (name) =>
  lazy(() => import("./admin").then((m) => ({ default: m[name] })));
const AdminLayout = adminLazy("AdminLayout");
const ProtectedRoute = adminLazy("ProtectedRoute");
const AdminLogin = adminLazy("AdminLogin");
const Dashboard = adminLazy("Dashboard");
const BlogList = adminLazy("BlogList");
const BlogForm = adminLazy("BlogForm");
const CaseStudyList = adminLazy("CaseStudyList");
const CaseStudyForm = adminLazy("CaseStudyForm");
const PodcastList = adminLazy("PodcastList");
const PodcastForm = adminLazy("PodcastForm");
const POCList = adminLazy("POCList");
const POCForm = adminLazy("POCForm");
const POCLeads = adminLazy("POCLeads");
const NewsList = adminLazy("NewsList");
const NewsForm = adminLazy("NewsForm");
const Categories = adminLazy("Categories");
const Contacts = adminLazy("Contacts");
const PendingApprovals = adminLazy("PendingApprovals");
const AdminUsers = adminLazy("AdminUsers");
const AdminTestimonials = adminLazy("Testimonials");
const Appreciations = adminLazy("Appreciations");
const AdminProductList = adminLazy("ProductList");
const AdminProductForm = adminLazy("ProductForm");
const AdminClients = adminLazy("Clients");
const AdminPolicyList = adminLazy("PolicyList");
const AdminPolicyForm = adminLazy("PolicyForm");
const AdminStats = adminLazy("Stats");
const AdminHero = adminLazy("Hero");
const AdminSuccessStories = adminLazy("SuccessStories");
const AdminProfile = adminLazy("AdminProfile");

// Lightweight fallback shown while a route chunk is being fetched. `.page-loader`
// (index.css) keeps it a full screen tall so the footer stays below the fold
// and doesn't jump when the page arrives (layout shift).
const PageLoader = () => (
  <div className="page-loader flex items-center justify-center w-full">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

// Layout wrapper component with sticky footer.
// `.public-zoom` applies a desktop-only CSS zoom (see index.css) for the roomy
// look while staying fully responsive; it doesn't wrap the admin panel.
// `.public-shell` (not Tailwind's min-h-screen) keeps the footer at the bottom
// of short pages: the zoom also scales vh, see index.css.
const PublicLayout = ({ children }) => {
  return (
    <div className="public-zoom">
      <div className="public-shell flex flex-col">
        <DefaultSeo />
        <Header />
        <ScrollToTop />
        <main className="flex-grow">
          {children}
        </main>
        <Footer />
      </div>
      <CookieConsent />
      <Analytics />
    </div>
  );
};

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <AuthProvider>
      {isAdminRoute ? (
        // Admin Routes - No Header/Footer
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              {/* /admin itself → the dashboard */}
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="blogs" element={<BlogList />} />
              <Route path="blogs/new" element={<BlogForm />} />
              <Route path="blogs/edit/:id" element={<BlogForm />} />
              <Route path="case-studies" element={<CaseStudyList />} />
              <Route path="case-studies/new" element={<CaseStudyForm />} />
              <Route path="case-studies/edit/:id" element={<CaseStudyForm />} />
              <Route path="podcasts" element={<PodcastList />} />
              <Route path="podcasts/new" element={<PodcastForm />} />
              <Route path="podcasts/edit/:id" element={<PodcastForm />} />
              <Route path="pocs" element={<POCList />} />
              <Route path="pocs/new" element={<POCForm />} />
              <Route path="pocs/edit/:id" element={<POCForm />} />
              <Route path="poc-leads" element={<POCLeads />} />
              <Route path="news" element={<NewsList />} />
              <Route path="news/new" element={<NewsForm />} />
              <Route path="news/edit/:id" element={<NewsForm />} />
              <Route path="categories" element={<Categories />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="appreciations" element={<Appreciations />} />
              <Route path="products" element={<AdminProductList />} />
              <Route path="products/new" element={<AdminProductForm />} />
              <Route path="products/edit/:id" element={<AdminProductForm />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="policies" element={<AdminPolicyList />} />
              <Route path="policies/new" element={<AdminPolicyForm />} />
              <Route path="policies/edit/:id" element={<AdminPolicyForm />} />
              <Route path="hero" element={<AdminHero />} />
              <Route path="stats" element={<AdminStats />} />
              <Route path="success-stories" element={<AdminSuccessStories />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route
                path="pending"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <PendingApprovals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="users"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              {/* Unknown /admin/* URL (typo, removed page) → the dashboard */}
              <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
            </Route>
          </Routes>
        </Suspense>
      ) : (
        // Public Routes - With Header/Footer
        <PublicLayout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/intern" element={<Internship />} />
              <Route path="/engineering" element={<Engineering />} />
              <Route path="/life" element={<Life />} />
              <Route path="/details/:jobId" element={<Details />} />
              <Route path="/supply" element={<Customer />} />
              <Route path="/pharma" element={<Pharma />} />
              <Route path="/semiconductor1" element={<Semiconductor />} />
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/blogdetails1" element={<BlogDetails1 />} />
              <Route path="/blogdetails2" element={<Blogdetails2 />} />
              <Route path="/blogdetails3" element={<Blogdetails3 />} />
              <Route path="/blogdetails4" element={<Blogdetails4 />} />
              <Route path="/blogdetails5" element={<Blogdetails5 />} />
              <Route path="/blogdetails6" element={<Blogdetails6 />} />
              <Route path="/blogdetails7" element={<Blogdetails7 />} />
              <Route path="/blogdetails8" element={<Blogdetails8 />} />
              <Route path="/blogdetails9" element={<Blogdetails9 />} />
              <Route path="/careerlanding" element={<CareerLanding />} />
              <Route path="/google-login" element={<GoogleLogin />} />
              <Route path="/blogs/:slug" element={<BlogDetails />} />
              {/* Comments were never wired to a live page (no <Outlet/>,
                  no blogId); old URLs go to the hub / the post instead. */}
              <Route path="/comments" element={<Navigate to="/blogs" replace />} />
              <Route path="/blogs/:slug/comments" element={<BlogSlugRedirect />} />
              <Route path="/industrylanding" element={<IndustryLanding />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:productSlug" element={<Products />} />
              <Route path="/tabbing" element={<Tabbing />} />
              <Route path="/barde" element={<Barde />} />
              <Route path="/TCApp" element={<TCApp />} />
              <Route path="/Bestowal" element={<BestowalWeb />} />
              <Route path="/relatedblog/:slug" element={<BlogSlugRedirect />} />
              <Route path="/blogs/category/:tag" element={<BlogTagSearch />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/testimonial/:id" element={<TestimonialDetail />} />
              <Route path="/employee-appreciation/:id" element={<EmployeeAppreciationDetail />} />
              <Route path="/AI-Machine-Learning-Solution" element={<Al />} />
              <Route path="/aeronautical" element={<Aeronautical />} />
              <Route path="/electronics" element={<Electronics />} />
              <Route path="/agrochemical" element={<Agrochemical />} />
              <Route path="/chemical" element={<Chemical />} />
              <Route path="/manufacturing" element={<Manufacturing />} />
              <Route path="/fintech" element={<Fintech />} />
              <Route path="/education-edtech" element={<Education />} />
              <Route path="/FMCG" element={<FMCG />} />
              <Route path="/sports" element={<Sports />} />
              <Route path="/packaging" element={<Packaging />} />
              <Route path="/pharmaceutical" element={<Pharmaceutical />} />
              <Route path="/retail" element={<Retail />} />
              <Route path="/semiconductor" element={<Semiconductor1 />} />
              <Route path="/automobile" element={<AutomobileIndustry />} />
              <Route path="/industry" element={<Industry />} />
              <Route path="/bestoservice" element={<Besto_Services />} />
              <Route path="/digital-core-transformation/:tabSlug?" element={<Services />} />
              <Route path="/intelligent-industry-solutions/:tabSlug?" element={<Services />} />
              <Route path="/data-cloud-transformation/:tabSlug?" element={<Services />} />
              <Route path="/digital-experience-engagement/:tabSlug?" element={<Services />} />
              <Route path="/product-engineering-innovation/:tabSlug?" element={<Services />} />
              <Route path="/flexible-talent-delivery-models/:tabSlug?" element={<Services />} />
              <Route path="/predosolutions" element={<PredoSolution />} />
              <Route path="/bestoflow" element={<BestoFlow />} />
              <Route path="/askochat" element={<AskOchat />} />
              <Route path="/zentick_ai" element={<Zenetick_AI />} />
              <Route path="/besto_jyotish" element={<BestoJyotish />} />
              {/* /blog/:id depended on router state nothing set — always "not found" */}
              <Route path="/blog/:id" element={<Navigate to="/blogs" replace />} />
              {/* Admin preview link (CaseStudyList) + old bookmarks; redirects
                  to /case-study/:slug once the item is approved. */}
              <Route path="/casestudy/:id" element={<Casestudyinfo />} />
              <Route path="/case-study/:slug" element={<Casestudyinfo />} />
              <Route path="/news/:slug" element={<NewsDetails />} />
              <Route path="/podcast/:id" element={<PodcastInfo />} />
              {/* The hard-coded /podcasts page (placeholder video ids) is
                  replaced by the CMS-driven Podcasts tab of the Insights hub. */}
              <Route path="/podcasts" element={<Navigate to="/blogs?tab=podcasts" replace />} />
              {/* Legal / compliance pages (CMS-managed via /admin/policies).
                  Fixed slugs get clean URLs; /policy/:slug covers any extra
                  pages an admin adds later (e.g. disclaimer). */}
              <Route path="/privacy-policy" element={<PolicyPage slug="privacy-policy" />} />
              <Route path="/cookie-policy" element={<PolicyPage slug="cookie-policy" />} />
              <Route path="/terms-of-use" element={<PolicyPage slug="terms-of-use" />} />
              <Route path="/policy/:slug" element={<PolicyPage />} />
              {/* Success Stories (CMS-managed via /admin/success-stories) —
                  listing + per-story detail page. Linked from the About menu. */}
              <Route path="/success-stories" element={<SuccessStories />} />
              <Route path="/success-stories/:slug" element={<SuccessStoryDetail />} />
              {/* Site-wide search results (header search box → /search?q=…) */}
              <Route path="/search" element={<SearchResults />} />
              {/* Removed service pages → closest live service tab */}
              {Object.entries(REMOVED_PAGE_REDIRECTS).map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}
              {/* Catch-all 404 — must stay last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </PublicLayout>
      )}
    </AuthProvider>
  );
}

export default App;
